/* Career Level Up: quiz (where you are → where you want to be) and Claude roadmap with progress ticks. */
import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Briefcase, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { CareerRoadmap } from "@/components/CareerRoadmap";
import CareerDeck from "@/components/CareerDeck";

export default function CareerSection() {
  const [questions, setQuestions] = useState([]);
  const [res, setRes] = useState(null);
  const [mode, setMode] = useState("loading");

  const load = useCallback(async () => {
    const [{ data: q }, { data: p }] = await Promise.all([api.get("/career/questions"), api.get("/career/plan")]);
    setQuestions(q.questions); setRes(p.plan); setMode(p.plan ? "plan" : "intro");
  }, []);
  useEffect(() => { load().catch(() => setMode("intro")); }, [load]);

  return (
    <section className="mt-14" data-testid="career-section">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Level Up · Career</div>
      <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">Where you are. Where you&apos;re going. How you get there.</h2>
      <p className="text-moss mt-3 max-w-2xl leading-relaxed text-sm">Eight questions about your work today and the work you want. You get a phased roadmap — skills, credentials, projects, people — with a realistic timeline and pay path. Tick steps off as you go and your career level climbs.</p>
      <div className="mt-6">
        {mode === "loading" && <div className="font-mono text-sm text-moss">loading…</div>}
        {mode === "intro" && (
          <div className="bg-linen border border-sand rounded-2xl p-8 text-center" data-testid="career-intro">
            <Briefcase className="w-8 h-8 text-gold mx-auto" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl mt-4">Map your next move</h3>
            <p className="text-sm text-moss mt-2">Two minutes. Retake every 30 days to re-plan.</p>
            <button onClick={() => setMode("quiz")} className="mt-6 bg-ink text-cream rounded-lg px-6 py-3 text-sm font-medium hover:bg-ink-deep inline-flex items-center gap-2" data-testid="career-start">Start the career quiz <ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
        {mode === "quiz" && <CareerQuiz questions={questions} initial={res?.answers} onDone={(d) => { setRes(d); setMode("plan"); }} onCancel={res ? () => setMode("plan") : null} />}
        {mode === "plan" && res && <CareerRoadmap res={res} onChanged={setRes} onRetake={() => setMode("quiz")} />}
      </div>
      <CareerDeck />
    </section>
  );
}

function CareerQuiz({ questions, initial, onDone, onCancel }) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const q = questions[i];
  if (!q) return null;
  const val = answers[q.id] || "";
  const set = (v) => setAnswers({ ...answers, [q.id]: v });
  const pick = (o) => { set(o); if (i < questions.length - 1) setTimeout(() => setI(i + 1), 160); };
  const submit = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/career/submit", { answers }); onDone(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const complete = questions.every((x) => (answers[x.id] || "").trim());
  return (
    <div className="bg-linen border border-sand rounded-2xl p-6 sm:p-8" data-testid="career-quiz">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
        <span data-testid="career-progress">Question {i + 1} of {questions.length}</span>
        {onCancel && <button onClick={onCancel} className="hover:text-ink" data-testid="career-quiz-cancel">Cancel</button>}
      </div>
      <div className="mt-2 h-1 rounded-full bg-sand-soft"><div className="h-full bg-ink rounded-full transition-all" style={{ width: `${((i + (val ? 1 : 0)) / questions.length) * 100}%` }} /></div>
      <h3 className="font-serif text-2xl sm:text-3xl mt-6" data-testid="career-question">{q.q}</h3>
      {q.type === "text" ? (
        <input value={val} onChange={(e) => set(e.target.value)} placeholder={q.placeholder} maxLength={200} autoFocus
          onKeyDown={(e) => { if (e.key === "Enter" && val.trim() && i < questions.length - 1) setI(i + 1); }}
          className="mt-5 w-full bg-white border border-sand rounded-xl px-4 py-3 text-base outline-none focus:ring-2 focus:ring-ink" data-testid={`career-text-${q.id}`} />
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 gap-2">
          {q.opts.map((o, idx) => (
            <button key={o} onClick={() => pick(o)} className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${val === o ? "bg-ink text-cream border-ink" : "bg-white border-sand hover:border-ink"}`} data-testid={`career-opt-${q.id}-${idx}`}>{o}</button>
          ))}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between gap-3">
        <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="inline-flex items-center gap-1.5 text-sm text-moss disabled:opacity-40" data-testid="career-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        {i < questions.length - 1 ? (
          <button onClick={() => setI(i + 1)} disabled={!val.trim()} className="inline-flex items-center gap-1.5 text-sm text-ink font-medium disabled:opacity-40" data-testid="career-next">Next <ArrowRight className="w-4 h-4" /></button>
        ) : (
          <button onClick={submit} disabled={!complete || busy} className="bg-ink text-cream rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-ink-deep disabled:opacity-50 inline-flex items-center gap-2" data-testid="career-submit">
            <Sparkles className="w-4 h-4" /> {busy ? "Mapping your path… (~20s)" : "Build my roadmap"}
          </button>
        )}
      </div>
      {err && <div className="mt-3 text-sm text-clay" data-testid="career-error">{err}</div>}
    </div>
  );
}
